// PhoneContact — a clickable phone number that opens a small "reach this
// person" sheet with Call / WhatsApp / Telegram / Copy actions, each with its
// real brand logo. Used in admin + agent ends on player and sub-agent profile
// cards and on the identity/overview header of a single player / sub-agent.
//
// Reuses the shared <Modal> (portals to <body>, so it works even when the
// trigger sits inside a router <Link> — the trigger itself stops propagation
// so clicking the number never navigates the underlying card).
import { useState } from 'react';
import Icon from './Icon';
import { Modal } from './Modal';

/** Build the per-channel deep links from a stored phone string.
 *  Phones are stored E.164 (+91XXXXXXXXXX) on this platform; we also tolerate
 *  a bare 10-digit local number by defaulting it to the India country code. */
function phoneLinks(raw: string) {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 10) digits = '91' + digits; // bare local → +91 (India platform)
  return {
    digits,
    tel: `tel:+${digits}`,
    // wa.me / Telegram want the international number WITHOUT a leading '+'.
    whatsapp: `https://wa.me/${digits}`,
    telegram: `tg://resolve?phone=${digits}`,
  };
}

function WhatsAppLogo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.485 3.488" />
    </svg>
  );
}

function TelegramLogo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212-.07-.062-.174-.041-.249-.024-.106.024-1.793 1.139-5.061 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function Channel({
  href, onClick, bg, logo, title, subtitle,
}: {
  href?: string; onClick?: () => void; bg: string; logo: React.ReactNode; title: string; subtitle: string;
}) {
  const inner = (
    <>
      <span className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0" style={{ backgroundColor: bg }}>
        {logo}
      </span>
      <span className="flex-1 text-left min-w-0">
        <span className="block font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</span>
        <span className="block text-[11.5px] text-neutral-500 truncate">{subtitle}</span>
      </span>
      <Icon name="chevron_right" size={20} className="text-neutral-400 shrink-0" />
    </>
  );
  const cls =
    'w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/60 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 active:scale-[0.99] transition';
  return href ? (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className={cls} onClick={(e) => e.stopPropagation()}>
      {inner}
    </a>
  ) : (
    <button type="button" className={cls} onClick={onClick}>{inner}</button>
  );
}

function ContactSheet({ phone, name, onClose }: { phone: string; name?: string | null; onClose: () => void }) {
  const { tel, whatsapp, telegram } = phoneLinks(phone);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { navigator.clipboard?.writeText(phone); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 leading-tight">Contact</h3>
          {name && <div className="text-sm text-neutral-500 truncate">{name}</div>}
          <div className="font-mono text-[15px] text-neutral-700 dark:text-neutral-300 mt-0.5 break-all">{phone}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close"
          className="shrink-0 w-8 h-8 grid place-items-center rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition">
          <Icon name="close" size={20} />
        </button>
      </div>
      <div className="space-y-2">
        <Channel href={tel}      bg="#16a34a" logo={<Icon name="call" size={22} />} title="Call"     subtitle="Open the phone dialer" />
        <Channel href={whatsapp} bg="#25D366" logo={<WhatsAppLogo />}                title="WhatsApp" subtitle="Open a WhatsApp chat" />
        <Channel href={telegram} bg="#229ED9" logo={<TelegramLogo />}                title="Telegram" subtitle="Open in Telegram (if installed)" />
        <Channel onClick={copy}  bg="#6b7280" logo={<Icon name={copied ? 'check' : 'content_copy'} size={20} />} title={copied ? 'Copied!' : 'Copy number'} subtitle="Copy to clipboard" />
      </div>
    </div>
  );
}

/**
 * Clickable phone number that opens the contact sheet.
 * @param phone     the stored phone string (E.164 or bare local). Renders nothing if empty.
 * @param name      optional display name shown in the sheet header.
 * @param className override the trigger button styling to match its host (card vs header).
 * @param showIcon  show the small call hint icon before the number (default true).
 * @param iconSize  size of that hint icon.
 */
export default function PhoneContact({
  phone, name, className, showIcon = true, iconSize = 11,
}: {
  phone: string | null | undefined;
  name?: string | null;
  className?: string;
  showIcon?: boolean;
  iconSize?: number;
}) {
  const [open, setOpen] = useState(false);
  if (!phone) return null;
  // Rendered as a clear interactive PILL so it never looks like plain text:
  // a green contact icon + the number + a ▾ chevron, with a border + hover.
  // The host's `className` still controls font/size/colour of the number.
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        title="Tap to Call / WhatsApp / Telegram / copy"
        className={`group inline-flex items-center gap-1 align-middle max-w-full rounded-md px-1.5 py-[1px]
          border border-black/10 dark:border-white/15 bg-black/[0.035] dark:bg-white/[0.06]
          hover:bg-emerald-500/10 hover:border-emerald-500/40 cursor-pointer transition ${className ?? ''}`}
      >
        {showIcon && <Icon name="contact_phone" size={iconSize} className="shrink-0 text-emerald-600 dark:text-emerald-400" />}
        <span className="truncate">{phone}</span>
        <Icon name="expand_more" size={Math.max(12, iconSize)} className="shrink-0 -ml-0.5 opacity-45 group-hover:opacity-80 transition" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ContactSheet phone={phone} name={name} onClose={() => setOpen(false)} />
      </Modal>
    </>
  );
}
